/**
 * The UI-side mirror of `backend/src/security/permission-types.ts` and the
 * resource table in
 * `docs/superpowers/specs/2026-07-25-team-roles-permissions-design.md`.
 *
 * The UI build has no import path into `backend/src`, so the enum, the
 * ladder helpers, `RESOURCE_SCOPE`, and `MEANINGFUL_LEVELS` are re-declared
 * here with the exact same string values rather than imported — the string
 * values are what round-trip through `GET /permissions/me`, so keeping this
 * file byte-for-byte aligned with the backend enum (values, scope, and which
 * levels are meaningful) is what makes the wire contract line up. Task 19's
 * role editor renders its 30 rows straight from `PERMISSION_CATALOG`, so
 * completeness here matters as much as it does on the backend.
 */

export enum PermissionLevel {
  VIEW = 'view',
  EDIT = 'edit',
  MANAGE = 'manage',
}

export enum PermissionResource {
  // Board & content — grantable per project
  PROJECT = 'project',
  PROJECT_CARD = 'project_card',
  PROJECT_COMMENT = 'project_comment',
  EPIC = 'epic',
  SPRINT = 'sprint',
  WIKI = 'wiki',
  DATABASE = 'database',
  FILE = 'file',
  // Agents & tools
  AGENT = 'agent',
  SKILL = 'skill',
  CUSTOM_TOOL = 'custom_tool',
  AGENT_TEST = 'agent_test',
  // Knowledge & data
  KNOWLEDGE = 'knowledge',
  TEAM_SECRET = 'team_secret',
  // Security & infrastructure
  WORKSPACE = 'workspace',
  NETWORK_POLICY = 'network_policy',
  SKILLS_POLICY = 'skills_policy',
  SSO = 'sso',
  DOMAIN = 'domain',
  MODEL_PROVIDER = 'model_provider',
  CHANNEL = 'channel',
  SCHEDULED_TASK = 'scheduled_task',
  EXTERNAL_WORK = 'external_work',
  // Communication
  TEAM_CHAT = 'team_chat',
  EXTERNAL_CONVERSATION = 'external_conversation',
  // Team & money
  APPROVAL = 'approval',
  OBSERVABILITY = 'observability',
  TEAM_SETTINGS = 'team_settings',
  TEAM_MEMBER = 'team_member',
  ROLE = 'role',
  BILLING = 'billing',
}

/** A role's stored shape: the single highest level held per resource. */
export type PermissionMap = Partial<
  Record<PermissionResource, PermissionLevel>
>;

export const LEVEL_ORDER: Record<PermissionLevel, number> = {
  [PermissionLevel.VIEW]: 1,
  [PermissionLevel.EDIT]: 2,
  [PermissionLevel.MANAGE]: 3,
};

/** True when `held` satisfies `required`. A missing level satisfies nothing. */
export function levelAtLeast(
  held: PermissionLevel | string | null | undefined,
  required: PermissionLevel,
): boolean {
  if (!held) return false;
  const heldOrder = LEVEL_ORDER[held as PermissionLevel];
  if (heldOrder === undefined) return false;
  return heldOrder >= LEVEL_ORDER[required];
}

/** The more permissive of two levels; null only when both are absent. */
export function maxLevel(
  a: PermissionLevel | null | undefined,
  b: PermissionLevel | null | undefined,
): PermissionLevel | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return LEVEL_ORDER[a] >= LEVEL_ORDER[b] ? a : b;
}

export type ResourceScope = 'team_only' | 'team_or_project';

/**
 * Where a grant of this resource can be made. `team_or_project` resources are
 * board content: a team-scoped grant covers every project present and
 * future, a project-scoped grant covers exactly one board.
 */
export const RESOURCE_SCOPE: Record<PermissionResource, ResourceScope> = {
  [PermissionResource.PROJECT]: 'team_or_project',
  [PermissionResource.PROJECT_CARD]: 'team_or_project',
  [PermissionResource.PROJECT_COMMENT]: 'team_or_project',
  [PermissionResource.EPIC]: 'team_or_project',
  [PermissionResource.SPRINT]: 'team_or_project',
  [PermissionResource.WIKI]: 'team_or_project',
  [PermissionResource.DATABASE]: 'team_or_project',
  [PermissionResource.FILE]: 'team_or_project',
  [PermissionResource.AGENT]: 'team_only',
  [PermissionResource.SKILL]: 'team_only',
  [PermissionResource.CUSTOM_TOOL]: 'team_only',
  [PermissionResource.AGENT_TEST]: 'team_only',
  [PermissionResource.KNOWLEDGE]: 'team_only',
  [PermissionResource.TEAM_SECRET]: 'team_only',
  [PermissionResource.WORKSPACE]: 'team_only',
  [PermissionResource.NETWORK_POLICY]: 'team_only',
  [PermissionResource.SKILLS_POLICY]: 'team_only',
  [PermissionResource.SSO]: 'team_only',
  [PermissionResource.DOMAIN]: 'team_only',
  [PermissionResource.MODEL_PROVIDER]: 'team_only',
  [PermissionResource.CHANNEL]: 'team_only',
  [PermissionResource.SCHEDULED_TASK]: 'team_only',
  [PermissionResource.EXTERNAL_WORK]: 'team_only',
  [PermissionResource.TEAM_CHAT]: 'team_only',
  [PermissionResource.EXTERNAL_CONVERSATION]: 'team_only',
  [PermissionResource.APPROVAL]: 'team_only',
  [PermissionResource.OBSERVABILITY]: 'team_only',
  [PermissionResource.TEAM_SETTINGS]: 'team_only',
  [PermissionResource.TEAM_MEMBER]: 'team_only',
  [PermissionResource.ROLE]: 'team_only',
  [PermissionResource.BILLING]: 'team_only',
};

const ALL: PermissionLevel[] = [
  PermissionLevel.VIEW,
  PermissionLevel.EDIT,
  PermissionLevel.MANAGE,
];
const VIEW_MANAGE: PermissionLevel[] = [
  PermissionLevel.VIEW,
  PermissionLevel.MANAGE,
];

/**
 * Which levels the editor renders as checkboxes. Anything omitted renders as
 * a dash — there is no such thing as, say, "edit the skills policy". Mirrors
 * `MEANINGFUL_LEVELS` in `backend/src/security/permission-types.ts` exactly;
 * do not invent a different split here.
 */
export const MEANINGFUL_LEVELS: Record<PermissionResource, PermissionLevel[]> =
  {
    [PermissionResource.PROJECT]: ALL,
    [PermissionResource.PROJECT_CARD]: ALL,
    [PermissionResource.PROJECT_COMMENT]: ALL,
    [PermissionResource.EPIC]: ALL,
    [PermissionResource.SPRINT]: ALL,
    [PermissionResource.WIKI]: ALL,
    [PermissionResource.DATABASE]: ALL,
    [PermissionResource.FILE]: ALL,
    [PermissionResource.AGENT]: ALL,
    [PermissionResource.SKILL]: ALL,
    [PermissionResource.CUSTOM_TOOL]: ALL,
    [PermissionResource.AGENT_TEST]: ALL,
    [PermissionResource.KNOWLEDGE]: ALL,
    [PermissionResource.TEAM_SECRET]: ALL,
    [PermissionResource.WORKSPACE]: ALL,
    [PermissionResource.NETWORK_POLICY]: ALL,
    [PermissionResource.SKILLS_POLICY]: VIEW_MANAGE,
    [PermissionResource.SSO]: VIEW_MANAGE,
    [PermissionResource.DOMAIN]: VIEW_MANAGE,
    [PermissionResource.MODEL_PROVIDER]: VIEW_MANAGE,
    [PermissionResource.CHANNEL]: VIEW_MANAGE,
    [PermissionResource.SCHEDULED_TASK]: ALL,
    [PermissionResource.EXTERNAL_WORK]: VIEW_MANAGE,
    [PermissionResource.TEAM_CHAT]: ALL,
    [PermissionResource.EXTERNAL_CONVERSATION]: ALL,
    [PermissionResource.APPROVAL]: VIEW_MANAGE,
    [PermissionResource.OBSERVABILITY]: VIEW_MANAGE,
    [PermissionResource.TEAM_SETTINGS]: ALL,
    [PermissionResource.TEAM_MEMBER]: ALL,
    [PermissionResource.ROLE]: ALL,
    [PermissionResource.BILLING]: ALL,
  };

export type PermissionGroupKey =
  | 'board_content'
  | 'agents_tools'
  | 'knowledge_data'
  | 'security_infra'
  | 'communication'
  | 'team_money';

export interface PermissionGroup {
  key: PermissionGroupKey;
  label: string;
  resources: PermissionResource[];
}

/**
 * The six groups the role editor collapses its 31 rows into, in the order the
 * spec lists them.
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: 'board_content',
    label: 'Board & Content',
    resources: [
      PermissionResource.PROJECT,
      PermissionResource.PROJECT_CARD,
      PermissionResource.PROJECT_COMMENT,
      PermissionResource.EPIC,
      PermissionResource.SPRINT,
      PermissionResource.WIKI,
      PermissionResource.DATABASE,
      PermissionResource.FILE,
    ],
  },
  {
    key: 'agents_tools',
    label: 'Agents & Tools',
    resources: [
      PermissionResource.AGENT,
      PermissionResource.SKILL,
      PermissionResource.CUSTOM_TOOL,
      PermissionResource.AGENT_TEST,
    ],
  },
  {
    key: 'knowledge_data',
    label: 'Knowledge & Data',
    resources: [PermissionResource.KNOWLEDGE, PermissionResource.TEAM_SECRET],
  },
  {
    key: 'security_infra',
    label: 'Security & Infrastructure',
    resources: [
      PermissionResource.WORKSPACE,
      PermissionResource.NETWORK_POLICY,
      PermissionResource.SKILLS_POLICY,
      PermissionResource.SSO,
      PermissionResource.DOMAIN,
      PermissionResource.MODEL_PROVIDER,
      PermissionResource.CHANNEL,
      PermissionResource.SCHEDULED_TASK,
      PermissionResource.EXTERNAL_WORK,
    ],
  },
  {
    key: 'communication',
    label: 'Communication',
    resources: [
      PermissionResource.TEAM_CHAT,
      PermissionResource.EXTERNAL_CONVERSATION,
    ],
  },
  {
    key: 'team_money',
    label: 'Team & Money',
    resources: [
      PermissionResource.APPROVAL,
      PermissionResource.OBSERVABILITY,
      PermissionResource.TEAM_SETTINGS,
      PermissionResource.TEAM_MEMBER,
      PermissionResource.ROLE,
      PermissionResource.BILLING,
    ],
  },
];

const RESOURCE_GROUP: Record<PermissionResource, PermissionGroupKey> = (() => {
  const map = {} as Record<PermissionResource, PermissionGroupKey>;
  for (const group of PERMISSION_GROUPS) {
    for (const resource of group.resources) {
      map[resource] = group.key;
    }
  }
  return map;
})();

/** Human label for each resource, shown as the row header in the editor. */
const RESOURCE_LABELS: Record<PermissionResource, string> = {
  [PermissionResource.PROJECT]: 'Projects',
  [PermissionResource.PROJECT_CARD]: 'Cards',
  [PermissionResource.PROJECT_COMMENT]: 'Card comments',
  [PermissionResource.EPIC]: 'Epics',
  [PermissionResource.SPRINT]: 'Sprints',
  [PermissionResource.WIKI]: 'Wiki',
  [PermissionResource.DATABASE]: 'Databases',
  [PermissionResource.FILE]: 'Files',
  [PermissionResource.AGENT]: 'Agents',
  [PermissionResource.SKILL]: 'Skills',
  [PermissionResource.CUSTOM_TOOL]: 'Custom Tools',
  [PermissionResource.AGENT_TEST]: 'Agent Tests',
  [PermissionResource.KNOWLEDGE]: 'Knowledge',
  [PermissionResource.TEAM_SECRET]: 'Secrets',
  [PermissionResource.WORKSPACE]: 'Workspaces',
  [PermissionResource.NETWORK_POLICY]: 'Network Policy',
  [PermissionResource.SKILLS_POLICY]: 'Skills Policy',
  [PermissionResource.SSO]: 'Single sign-on',
  [PermissionResource.DOMAIN]: 'Domains',
  [PermissionResource.MODEL_PROVIDER]: 'Model Providers',
  [PermissionResource.CHANNEL]: 'Channels',
  [PermissionResource.SCHEDULED_TASK]: 'Scheduled Tasks',
  [PermissionResource.EXTERNAL_WORK]: 'External Work',
  [PermissionResource.TEAM_CHAT]: 'Team Chat',
  [PermissionResource.EXTERNAL_CONVERSATION]: 'External Conversations',
  [PermissionResource.APPROVAL]: 'Approvals',
  [PermissionResource.OBSERVABILITY]: 'Observability',
  [PermissionResource.TEAM_SETTINGS]: 'Team Settings',
  [PermissionResource.TEAM_MEMBER]: 'Members',
  [PermissionResource.ROLE]: 'Roles',
  [PermissionResource.BILLING]: 'Billing',
};

/** One-line description shown under the label in the editor. */
const RESOURCE_DESCRIPTIONS: Record<PermissionResource, string> = {
  [PermissionResource.PROJECT]:
    'Projects (boards) themselves — existence, settings, and creation.',
  [PermissionResource.PROJECT_CARD]:
    'Cards, tasks, and attachments on a board.',
  [PermissionResource.PROJECT_COMMENT]:
    'Card comments — edit archives; manage permanently deletes archived comments.',
  [PermissionResource.EPIC]: 'Epics that group related cards.',
  [PermissionResource.SPRINT]: 'Sprints and the cards assigned to them.',
  [PermissionResource.WIKI]: 'A project’s wiki pages.',
  [PermissionResource.DATABASE]: 'Project databases, tables, and charts.',
  [PermissionResource.FILE]: 'Files and folders stored on a project.',
  [PermissionResource.AGENT]:
    'Agent configuration, prompts, models, and tool bundles.',
  [PermissionResource.SKILL]: 'Skills available to agents.',
  [PermissionResource.CUSTOM_TOOL]:
    'Custom tool definitions and their credentials.',
  [PermissionResource.AGENT_TEST]: 'Agent test suites and their results.',
  [PermissionResource.KNOWLEDGE]:
    'Knowledge sources, extracted documents, and the knowledge graph.',
  [PermissionResource.TEAM_SECRET]:
    'View secret names and request secrets; edit to answer protected requests; manage to administer the vault. Values are never shown to agents.',
  [PermissionResource.WORKSPACE]:
    'Sandboxed workspaces agents launch and run commands in.',
  [PermissionResource.NETWORK_POLICY]:
    'Egress mode and the domain/port allowlist agents may reach.',
  [PermissionResource.SKILLS_POLICY]:
    'Thresholds and kill switch for importing skills from providers.',
  [PermissionResource.SSO]:
    'Login mode for a verified domain and break-glass access when it is enforced.',
  [PermissionResource.DOMAIN]:
    'Claiming, verifying, removing, and auto-join for a team’s email domains.',
  [PermissionResource.MODEL_PROVIDER]:
    'Model provider connections and the team’s default models.',
  [PermissionResource.CHANNEL]: 'Slack and SMS channel connections.',
  [PermissionResource.SCHEDULED_TASK]: 'Scheduled (cron-like) agent tasks.',
  [PermissionResource.EXTERNAL_WORK]:
    'Authorized external (human) work sessions.',
  [PermissionResource.TEAM_CHAT]: 'The team chat dock and its conversations.',
  [PermissionResource.EXTERNAL_CONVERSATION]:
    'Agent–third-party email, SMS, and Slack threads.',
  [PermissionResource.APPROVAL]: 'Pending approvals and deciding them.',
  [PermissionResource.OBSERVABILITY]:
    'Metrics, action logs, jobs, and usage/cost.',
  [PermissionResource.TEAM_SETTINGS]:
    'Team name, description, working style, and retention defaults.',
  [PermissionResource.TEAM_MEMBER]:
    'Team membership, pending invites, and removal.',
  [PermissionResource.ROLE]: 'Roles and who is assigned them.',
  [PermissionResource.BILLING]:
    'Spend, usage cost, invoices, payment methods, and budgets.',
};

export interface PermissionCatalogEntry {
  resource: PermissionResource;
  label: string;
  description: string;
  group: PermissionGroupKey;
  scope: ResourceScope;
  meaningfulLevels: PermissionLevel[];
}

/**
 * The row data for the role editor and any other UI that
 * needs to describe a resource to a human. One entry per
 * `PermissionResource` member — see the parity test.
 */
export const PERMISSION_CATALOG: Record<
  PermissionResource,
  PermissionCatalogEntry
> = Object.values(PermissionResource).reduce(
  (catalog, resource) => {
    catalog[resource] = {
      resource,
      label: RESOURCE_LABELS[resource],
      description: RESOURCE_DESCRIPTIONS[resource],
      group: RESOURCE_GROUP[resource],
      scope: RESOURCE_SCOPE[resource],
      meaningfulLevels: MEANINGFUL_LEVELS[resource],
    };
    return catalog;
  },
  {} as Record<PermissionResource, PermissionCatalogEntry>,
);
